import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/ui/Button';
import { useGetAuthApi, usePutAuthApi } from '@/hooks/UseApiCalls';

interface ProjectNotificationSettingsDto {
  addresses: string;
}

/**
 * Who the office's project mails go to, on the approvals screen (the question "who hears about what
 * lands in this queue" belongs next to the queue):
 * - new projects and suppliers (HAT-1105) - management/projects/notification-settings;
 * - referrals to the legal department (SPR-10302) - management/projects/legal-notification-settings.
 *
 * One frame with the two lists side by side and one save button (SPR-10302: two stacked cards read
 * as two unrelated settings). Empty means nobody is told, which is how every installation starts -
 * so each field says so instead of leaving a blank box.
 *
 * Mirrors the server's rule on purpose: a token without an @ is refused here, by name, instead of
 * being saved and then silently ignored by the sender.
 */
const SEPARATORS = /[,;\s]+/;

const invalidTokens = (raw: string): string[] =>
  raw.split(SEPARATORS).map((token) => token.trim()).filter((token) => token.length > 0 && !token.includes('@'));

/** One list of addresses: what is loaded, what is typed, and saving it. */
const useAddressSetting = (url: string) => {
  const { data, isLoading } = useGetAuthApi<ProjectNotificationSettingsDto>(url);
  const { mutateAsync: put } = usePutAuthApi<ProjectNotificationSettingsDto, object, ProjectNotificationSettingsDto>(url);

  const [value, setValue] = useState('');
  const [saved, setSaved] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setValue(data.addresses ?? '');
      setSaved(data.addresses ?? '');
    }
  }, [data]);

  const isDirty = value.trim() !== saved.trim();

  /** Saves when changed. Returns false when the value was refused or the save failed. */
  const save = async (invalidMessage: (addresses: string) => string, failedMessage: string): Promise<boolean> => {
    if (!isDirty) return true;
    const bad = invalidTokens(value);
    if (bad.length > 0) {
      setError(invalidMessage(bad.join(', ')));
      return false;
    }
    setError(null);
    try {
      const result = await put({ body: { addresses: value } });
      // What the server stored is normalised (one separator, no duplicates); show that.
      setValue(result.addresses);
      setSaved(result.addresses);
      return true;
    } catch {
      setError(failedMessage);
      return false;
    }
  };

  return { value, setValue, saved, error, setError, isLoading, isDirty, save };
};

type AddressSetting = ReturnType<typeof useAddressSetting>;

interface AddressFieldProps {
  id: string;
  label: string;
  hint: string;
  placeholder: string;
  setting: AddressSetting;
  onEdit: () => void;
}

/** Label on the right, addresses on the left - one compact row per list. */
const AddressField = ({ id, label, hint, placeholder, setting, onEdit }: AddressFieldProps) => (
  <div className="flex flex-col gap-1 min-w-0">
    <div className="flex items-center gap-3 min-w-0">
      <label htmlFor={id} className="body-sm-semibold text-(--font-primary) whitespace-nowrap shrink-0">{label}</label>
      <input
        id={id}
        type="text"
        dir="ltr"
        disabled={setting.isLoading}
        aria-invalid={!!setting.error}
        aria-description={hint}
        title={hint}
        className="flex-1 min-w-0 border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
        placeholder={placeholder}
        value={setting.value}
        onChange={(event) => {
          setting.setValue(event.target.value);
          setting.setError(null);
          onEdit();
        }}
      />
    </div>
    {setting.error && <p role="alert" className="text-red-500 body-sm-regular">{setting.error}</p>}
  </div>
);

const ProjectNotificationSettingsCard = () => {
  const { t } = useTranslation();
  const office = useAddressSetting('management/projects/notification-settings');
  const legal = useAddressSetting('management/projects/legal-notification-settings');

  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const isDirty = office.isDirty || legal.isDirty;
  const isLoading = office.isLoading || legal.isLoading;

  // Which lists are saved empty - those mails are switched off, and the card says so beside the save
  // button (also right after saving an empty list: "saved" alone would not tell what that did).
  const emptyLists = isLoading
    ? []
    : [
        office.saved.trim() === '' ? t('projectsManagement.notifications.officeLabel') : null,
        legal.saved.trim() === '' ? t('projectsManagement.notifications.legalLabel') : null,
      ].filter((name): name is string => name !== null);

  const handleSave = async () => {
    setJustSaved(false);
    setIsSaving(true);
    const invalid = (addresses: string) => t('projectsManagement.notifications.invalid', { addresses });
    const failed = t('projectsManagement.notifications.saveFailed');
    // Both, even when the first is refused: each field shows its own result.
    const results = await Promise.all([office.save(invalid, failed), legal.save(invalid, failed)]);
    setIsSaving(false);
    setJustSaved(results.every(Boolean));
  };

  return (
    <section
      aria-labelledby="project-notifications-title"
      className="flex flex-col gap-3 border border-(--border-main) rounded-lg px-4 py-3 mb-6 w-full"
    >
      <div className="flex items-baseline gap-x-3 gap-y-1 flex-wrap">
        <h3 id="project-notifications-title" className="body-lr-semibold text-(--font-primary)">
          {t('projectsManagement.notifications.sectionTitle')}
        </h3>
        <p className="body-sm-regular text-(--font-secondary)">{t('projectsManagement.notifications.sectionDescription')}</p>
      </div>

      <div className="grid grid-cols-2 max-md:grid-cols-1 gap-x-8 gap-y-3">
        <AddressField
          id="project-notification-addresses"
          label={t('projectsManagement.notifications.officeLabel')}
          hint={t('projectsManagement.notifications.description')}
          placeholder={t('projectsManagement.notifications.placeholder')}
          setting={office}
          onEdit={() => setJustSaved(false)}
        />
        <AddressField
          id="project-legal-notification-addresses"
          label={t('projectsManagement.notifications.legalLabel')}
          hint={t('projectsManagement.legalNotifications.description')}
          placeholder={t('projectsManagement.legalNotifications.placeholder')}
          setting={legal}
          onEdit={() => setJustSaved(false)}
        />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="body-xs-regular text-(--font-secondary)">
          {emptyLists.length > 0 && t('projectsManagement.notifications.noneSetFor', { lists: emptyLists.join(', ') })}
        </p>
        <div className="flex items-center gap-3">
          {justSaved && <p role="status" className="body-sm-regular text-(--font-secondary)">{t('projectsManagement.notifications.saved')}</p>}
          <Button variant="primary" onClick={handleSave} disabled={!isDirty || isSaving || isLoading}>
            {t('projectsManagement.notifications.save')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProjectNotificationSettingsCard;
